import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { usePlan } from "../../providers/PlanProvider";

// Rota onde a empresa consegue regularizar o pagamento / mudar de plano.
// O overlay NUNCA pode travar esta tela, senão a empresa fica presa sem conseguir pagar.
const PAYMENT_ROUTE = "/companies/settings/change-plan";

// Copy diferenciada por motivo de bloqueio.
const getBlockCopy = (blockReason) => {
  if (blockReason === "trial_expired") {
    return {
      title: "Seu período de teste terminou",
      description:
        "O período de teste gratuito da sua conta chegou ao fim. Para continuar usando a plataforma e acessar os talentos, escolha um plano e finalize o pagamento.",
    };
  }

  // payment_overdue e qualquer outro motivo de bloqueio caem aqui.
  return {
    title: "Pagamento pendente",
    description:
      "Identificamos uma fatura em aberto na sua conta e o acesso à plataforma foi bloqueado. Regularize o pagamento para voltar a usar todos os recursos.",
  };
};

const BlockedOverlay = () => {
  const { planData } = usePlan();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Só age quando a empresa está efetivamente bloqueada.
  if (!planData?.blocked) {
    return null;
  }

  const { title, description } = getBlockCopy(planData?.blockReason);
  const isOnPaymentScreen = pathname === PAYMENT_ROUTE;

  const handleRegularize = () => {
    navigate(PAYMENT_ROUTE);
  };

  // Na própria tela de pagamento, mostramos apenas um banner de aviso no topo,
  // sem travar a interação com o formulário de regularização.
  if (isOnPaymentScreen) {
    return (
      <Box
        role="alert"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 13000,
          backgroundColor: "#B3261E",
          color: "#FFFFFF",
          px: 3,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          fontFamily: "IBM Plex Sans",
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
          pointerEvents: "none",
        }}
      >
        <Typography
          sx={{
            fontFamily: "IBM Plex Sans",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          {planData?.blockReason === "trial_expired"
            ? "Seu período de teste terminou. Escolha um plano abaixo para reativar o acesso."
            : "Acesso bloqueado por fatura em aberto. Finalize o pagamento abaixo para regularizar."}
        </Typography>
      </Box>
    );
  }

  // Overlay em tela cheia, não fechável: bloqueia toda a interação com o app.
  return (
    <Box
      role="dialog"
      aria-modal="true"
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 13000,
        backgroundColor: "rgba(20, 20, 20, 0.72)",
        backdropFilter: "blur(3px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        // Garante que nenhum clique passe para a árvore abaixo do overlay.
        pointerEvents: "auto",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "16px",
          maxWidth: "460px",
          width: "100%",
          p: { xs: 3, sm: 4 },
          boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
          textAlign: "center",
          fontFamily: "IBM Plex Sans",
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            backgroundColor: "#FDECEA",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px",
          }}
        >
          <svg
            width="30"
            height="30"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#B3261E"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
        </Box>

        <Typography
          sx={{
            fontFamily: "IBM Plex Sans",
            fontSize: "20px",
            fontWeight: 600,
            color: "#1D1D1D",
            mb: 1.5,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            fontFamily: "IBM Plex Sans",
            fontSize: "14px",
            fontWeight: 300,
            color: "#4A4A4A",
            lineHeight: 1.6,
            mb: 3,
          }}
        >
          {description}
        </Typography>

        <Button
          onClick={handleRegularize}
          fullWidth
          sx={{
            backgroundColor: "#EC5569",
            color: "#FFFFFF",
            textTransform: "none",
            fontFamily: "IBM Plex Sans",
            fontSize: "15px",
            fontWeight: 600,
            borderRadius: "80px",
            py: 1.5,
            "&:hover": {
              backgroundColor: "#d94458",
            },
          }}
        >
          Regularizar pagamento
        </Button>
      </Box>
    </Box>
  );
};

export default BlockedOverlay;
